import { formatUsd } from '@/utils/format';
import { isFiniteNumber } from '@/utils/number';
import { SESSION_LABELS } from './mm-program-editing';
import type { ProgramInput } from './mm-program-schema';
import { ChartFigure } from './program-charts';

const BUCKETS = ['b1', 'b2'] as const;
// The track spans a little more than the widest band so its edges never touch the frame.
const TRACK_SPAN = 1.15;

type Bucket = ProgramInput['b1'];

const describeBucket = (bucket: Bucket) =>
  `±${isFiniteNumber(bucket.bps) ? bucket.bps : '—'} bps · ${isFiniteNumber(bucket.kpi) ? formatUsd(bucket.kpi, 0) : '—'}${bucket.nbbo ? ' + ½ NBBO' : ''}`;

const isSameBucket = (left: Bucket, right: Bucket | undefined) =>
  right !== undefined && left.bps === right.bps && left.kpi === right.kpi && Boolean(left.nbbo) === Boolean(right.nbbo);

const bandStyle = (bps: number, halfRange: number) => {
  const half = isFiniteNumber(bps) ? (Math.max(bps, 0) / halfRange) * 50 : 0;
  return { left: `${50 - half}%`, width: `${half * 2}%` };
};

interface BandRowProps {
  name: string;
  draft: Bucket;
  saved?: Bucket;
  halfRange: number;
}

const BandRow = ({ name, draft, saved, halfRange }: BandRowProps) => {
  const isChanged = saved !== undefined && !isSameBucket(draft, saved);
  return (
    <div className="grid grid-cols-[1.75rem_minmax(0,1fr)] items-center gap-x-3 gap-y-1 sm:grid-cols-[1.75rem_minmax(0,1fr)_minmax(0,11rem)]">
      <span className="text-xs font-medium text-muted-foreground">{name}</span>
      <div aria-hidden className="relative h-8 rounded-sm bg-card">
        <span className="absolute inset-y-0 left-1/2 w-px bg-foreground/30" />
        <span className="absolute inset-y-1.5 rounded-[3px] border-x-2 border-primary bg-primary/20" style={bandStyle(draft.bps, halfRange)} />
        {isChanged ? <span className="absolute inset-y-0 border-x-2 border-muted-foreground/80" style={bandStyle(saved.bps, halfRange)} /> : null}
      </div>
      <p className="col-start-2 flex flex-col text-xs tabular-nums sm:col-start-3">
        <span>{describeBucket(draft)}</span>
        {isChanged ? <span className="text-muted-foreground">was {describeBucket(saved)}</span> : null}
      </p>
    </div>
  );
};
BandRow.displayName = 'BandRow';

interface BucketBandsProps {
  /** One schedule, or a C/E/W set ordered C, E, W */
  programs: ProgramInput[];
  saved?: ProgramInput[];
}

export const BucketBands = ({ programs, saved }: BucketBandsProps) => {
  const widths = [...programs, ...(saved ?? [])].flatMap((program) => BUCKETS.map((bucket) => program[bucket].bps)).filter(isFiniteNumber);
  const halfRange = Math.max(...widths, 1) * TRACK_SPAN;
  const isCompared = saved !== undefined && programs.some((program, position) => BUCKETS.some((bucket) => !isSameBucket(program[bucket], saved[position]?.[bucket])));
  const edge = Math.round(halfRange);
  return (
    <ChartFigure isCompared={isCompared} title="Buckets around mid">
      <div className="flex flex-col gap-4">
        {programs.map((program, position) => (
          <div className="flex flex-col gap-1.5" key={program.session ?? 'all'}>
            {program.session ? <span className="text-xs font-medium">{SESSION_LABELS[program.session] ?? program.session}</span> : null}
            {BUCKETS.map((bucket) => (
              <BandRow draft={program[bucket]} halfRange={halfRange} key={bucket} name={bucket.toUpperCase()} saved={saved?.[position]?.[bucket]} />
            ))}
          </div>
        ))}
        <div
          aria-hidden
          className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3 text-[11px] text-muted-foreground tabular-nums sm:grid-cols-[1.75rem_minmax(0,1fr)_minmax(0,11rem)]"
        >
          <span className="col-start-2 flex justify-between">
            <span>−{edge} bps</span>
            <span>mid</span>
            <span>+{edge} bps</span>
          </span>
        </div>
      </div>
    </ChartFigure>
  );
};
BucketBands.displayName = 'BucketBands';
